import { EmailTextInput, FloatingFormItem } from '@/components/_common';
import { ContactType } from '@/core/domain/_shared/ContactType';
import {
  PurchaseMailSendingForm,
  SendPurchaseEnum,
} from '@/core/domain/mail/MailControler/MailControler.types';
import { useSupplier } from '@/core/domain/people/Suppliers/Suppliers.hooks';
import { Purchase } from '@/core/domain/purchase/Purchase.types';
import { useReportTemplateGeneratePurchase } from '@/core/domain/reports/movements/purchase/ReportPurchase.hooks';
import { useDefaultEmptyFieldError } from '@/hooks';
import { useReleasePurchaseFormContext } from '@/pages/MovementsPage/PurchasesPage/ReleasesPage/ReleasesPurchaseProvider';

import {
  AutoComplete,
  Card,
  Col,
  Form,
  FormInstance,
  Input,
  Progress,
  Radio,
  Typography,
} from 'antd';
import { useEffect } from 'react';

type Props = {
  form: FormInstance<PurchaseMailSendingForm>;
  purchase?: Purchase;
};

const PurchaseSendEmailForm: React.FC<Props> = ({ form, purchase }) => {
  const { Title } = Typography;

  const { purchaseId } = useReleasePurchaseFormContext();
  const defaultEmptyFieldError = useDefaultEmptyFieldError();
  const sendPurchase = Form.useWatch<SendPurchaseEnum>('sendPurchase', form);
  const attachment = Form.useWatch<boolean>('attachment', form);

  const { data, isFetching } = useReportTemplateGeneratePurchase({
    purchaseReleaseId: Number(purchaseId),
    format: 'PDF',
    enabled: sendPurchase ? sendPurchase !== SendPurchaseEnum.none : false,
    showAdditionalData: sendPurchase === SendPurchaseEnum.withAdditionalData,
    showLocation: false,
  });

  useEffect(() => {
    if (data) {
      form.setFieldsValue({
        attachment: Buffer.from(data).toString('base64'),
      });
    }
  }, [data]);

  const { data: supplier } = useSupplier(purchase?.supplierDTO.id);

  const contacts = supplier?.contactList
    ?.map((contact) => contact.detailList)
    .flat()
    .filter((contact) => contact.contactType === ContactType.Email)
    .map((contact) => ({ value: contact.value }));

  return (
    <Form
      form={form}
      onValuesChange={(field: PurchaseMailSendingForm) => {
        if (field.sendPurchase === SendPurchaseEnum.none) {
          form.setFieldValue('attachment', null);
        }
      }}
    >
      <Form.Item hidden name="fromName" noStyle>
        <Input />
      </Form.Item>
      <Form.Item hidden name="attachment" noStyle>
        <Input />
      </Form.Item>

      <FloatingFormItem label="Para" name="to" rules={[defaultEmptyFieldError]}>
        <AutoComplete
          options={
            supplier?.email
              ? contacts?.concat({ value: supplier.email })
              : contacts
          }
          allowClear
        />
      </FloatingFormItem>

      <FloatingFormItem
        label="Assunto"
        name="subject"
        rules={[defaultEmptyFieldError]}
      >
        <Input />
      </FloatingFormItem>

      <Form.Item hidden name="message">
        <EmailTextInput style={{ height: 300 }} />
      </Form.Item>

      <>
        <Col span={24}>
          <Title level={5}>Opções de envio:</Title>

          <FloatingFormItem
            name="sendPurchase"
            initialValue={SendPurchaseEnum.none}
            getValueFromEvent={({ target }) => {
              if (target.value === SendPurchaseEnum.none) {
                form.setFieldValue('attachment', undefined);
              }

              return target.value;
            }}
          >
            <Radio.Group optionType="button" buttonStyle="solid">
              <Radio value={SendPurchaseEnum.none}>Não enviar anexo</Radio>

              <Radio
                value={SendPurchaseEnum.withAdditionalData}
                disabled={
                  sendPurchase === SendPurchaseEnum.withoutAdditionalData
                }
              >
                PDF completo (com dados adicionais)
              </Radio>

              <Radio
                value={SendPurchaseEnum.withoutAdditionalData}
                disabled={sendPurchase === SendPurchaseEnum.withAdditionalData}
              >
                PDF simplificado (sem dados adicionais)
              </Radio>
            </Radio.Group>
          </FloatingFormItem>
        </Col>

        {isFetching && (
          <Card bordered>
            <div>Importando o PDF da compra</div>
            <Progress percent={isFetching ? 25 : 50} showInfo={false} />
          </Card>
        )}

        {!isFetching &&
          attachment &&
          sendPurchase !== SendPurchaseEnum.none && (
            <Card bordered>PDF da compra anexado</Card>
          )}
      </>
    </Form>
  );
};

export default PurchaseSendEmailForm;
